import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ProductsSection = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Dynamic product data based on language
  const products = [
    {
      id: 1,
      name: t.products.product1.name,
      description: t.products.product1.description,
      spec: t.products.product1.spec,
      apps: t.products.product1.apps,
      image: "/images/gluconic-acid.jpg"
    },
    {
      id: 2,
      name: t.products.product2.name,
      description: t.products.product2.description,
      spec: t.products.product2.spec,
      apps: t.products.product2.apps,
      image: "/images/sodium-gluconate.jpg"
    },
    {
      id: 3,
      name: t.products.product3.name,
      description: t.products.product3.description,
      spec: t.products.product3.spec,
      apps: t.products.product3.apps,
      image: "/images/gdl-product.jpg"
    }
  ];

  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.products.title}</h2>
          <div className="w-20 h-1 bg-teal-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t.products.description}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <Card
              key={product.id}
              className="overflow-hidden hover:shadow-2xl transition-all duration-300 group border-0 shadow-lg cursor-pointer"
              onClick={() => navigate(`/product/${product.id}`)}
            >
              {/* Product Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {product.name}
                  </h3>
                </div>
              </div>

              {/* Product Details */}
              <div className="p-6">
                <p className="text-gray-600 mb-3 leading-relaxed line-clamp-3">
                  {product.description}
                </p>
                
                {/* Specifications */}
                <div className="mb-4 p-3 bg-teal-50 rounded-lg">
                  <p className="text-sm font-semibold text-teal-800">
                    {product.spec}
                  </p>
                </div>

                {/* Applications */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {t.products.usageAreas}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {product.apps.slice(0, 3).map((app, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors text-xs"
                      >
                        {app}
                      </Badge>
                    ))}
                    {product.apps.length > 3 && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                        +{product.apps.length - 3}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* View Details Button */}
                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white group/btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/product/${product.id}`);
                  }}
                >
                  {t.products.viewDetails}
                  <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Additional Info Accordion */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t.products.faqTitle}
          </h3>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="border-b border-gray-200">
              <AccordionTrigger className="text-left hover:text-teal-600 transition-colors">
                {t.products.faq1Q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                {t.products.faq1A}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-b border-gray-200">
              <AccordionTrigger className="text-left hover:text-teal-600 transition-colors">
                {t.products.faq2Q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                {t.products.faq2A}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-b border-gray-200">
              <AccordionTrigger className="text-left hover:text-teal-600 transition-colors">
                {t.products.faq3Q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                {t.products.faq3A}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-b border-gray-200">
              <AccordionTrigger className="text-left hover:text-teal-600 transition-colors">
                {t.products.faq4Q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                {t.products.faq4A}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
